import React, { useState } from 'react';
import { ChevronDown, HelpCircle, MessageSquare } from 'lucide-react';
import { WHATSAPP_MESSAGE_URL } from '../constants';

export const FaqSection = () => {
  const [openIdx, setOpenIdx] = useState(0);

  const faqs = [
    {
      question: '¿Qué es PastoreApp?',
      answer: 'PastoreApp es un software de gestión y administración para iglesias que permite organizar miembros, grupos celulares, formación, asistencia, finanzas y liderazgo ministerial en una sola plataforma.',
    },
    {
      question: '¿Qué es un software de gestión para iglesias?',
      answer: 'Es una herramienta digital diseñada para ayudar a las iglesias a centralizar la información de su congregación, equipos ministeriales y procesos administrativos en un solo lugar accesible y organizado.',
    },
    {
      question: '¿Cómo administrar los miembros de una iglesia?',
      answer: 'PastoreApp incluye un directorio completo de miembros con fichas individuales, datos de contacto, estados de actividad, segmentación por grupos de afinidad y seguimiento de su proceso formativo.',
    },
    {
      question: '¿Puedo gestionar grupos y células con PastoreApp?',
      answer: 'Sí. Puedes crear y administrar grupos comunitarios, asignar líderes, tomar asistencia semanal, geolocalizar reuniones y visualizar el crecimiento de cada grupo.',
    },
    {
      question: '¿Puedo controlar la asistencia de los miembros?',
      answer: 'Sí. PastoreApp permite registrar asistencia tanto en reuniones generales como en grupos celulares, formación y eventos especiales, con reportes por ministerio.',
    },
    {
      question: '¿Puedo gestionar escuelas de formación y discipulado?',
      answer: 'Sí. La plataforma incluye niveles formativos, inscripciones, cohortes, registro de lecciones y seguimiento del avance de cada estudiante hasta su graduación.',
    },
    {
      question: '¿Puedo registrar diezmos y ofrendas?',
      answer: 'Sí. PastoreApp tiene un módulo financiero para registrar diezmos, ofrendas, semillas, fondos especiales y generar informes de auditoría en PDF con verificación.',
    },
    {
      question: '¿PastoreApp sirve para iglesias pequeñas?',
      answer: 'Sí. PastoreApp está diseñado para iglesias de cualquier tamaño. Puedes comenzar con los módulos que necesitas y escalar a medida que tu congregación crece.',
    },
    {
      question: '¿PastoreApp sirve para iglesias grandes?',
      answer: 'Sí. La plataforma soporta múltiples ministerios, redes de liderazgo, gran cantidad de miembros y procesos complejos con permisos por rol.',
    },
    {
      question: '¿Puedo gestionar líderes y servidores?',
      answer: 'Sí. PastoreApp permite definir la estructura de liderazgo, asignar roles, crear redes de supervisión y hacer seguimiento del servicio de cada servidor.',
    },
    {
      question: '¿Puedo administrar diferentes ministerios desde una sola plataforma?',
      answer: 'Sí. PastoreApp integra alabanza, formación, consejería, finanzas, tickets, comunicação y más en un ecosistema conectado.',
    },
    {
      question: '¿Cómo puedo solicitar una demostración de PastoreApp?',
      answer: 'Puedes hacer clic en el botón "Solicitar Demostración" o "Contactanos" para comunicarte directamente por WhatsApp y recibir una demostración personalizada de la plataforma.',
    },
  ];

  const toggleFaq = (idx) => {
    setOpenIdx(openIdx === idx ? null : idx);
  };

  return (
    <section id="faq" className="py-20 bg-white dark:bg-slate-900 border-t border-slate-200/60 dark:border-slate-800/60">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 text-xs font-black uppercase tracking-widest border border-indigo-200 dark:border-indigo-800/80">
            <HelpCircle size={14} className="text-indigo-600 dark:text-indigo-400" />
            Preguntas Frecuentes
          </div>
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
            Resuelve tus dudas sobre PastoreApp
          </h2>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, idx) => {
            const isOpen = openIdx === idx;
            return (
              <div key={idx} className="bg-slate-50 dark:bg-slate-950 border border-slate-200/80 dark:border-slate-800/80 rounded-2xl overflow-hidden transition-all">
                <button
                  onClick={() => toggleFaq(idx)}
                  className="w-full p-5 text-left flex items-center justify-between gap-4 font-black text-slate-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  aria-expanded={isOpen}
                >
                  <h3 className="text-sm sm:text-base font-black">{faq.question}</h3>
                  <ChevronDown size={18} className={`shrink-0 text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-indigo-600 dark:text-indigo-400' : ''}`} />
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-300 leading-relaxed border-t border-slate-200/40 dark:border-slate-800/40 pt-4 animate-in fade-in duration-300">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="p-8 rounded-[2rem] bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-900 text-center space-y-4">
          <h3 className="text-lg font-black text-slate-900 dark:text-white">¿Tienes alguna inquietud específica?</h3>
          <p className="text-xs font-medium text-slate-600 dark:text-slate-400 max-w-xl mx-auto">
            Habla directamente con nuestro equipo para conocer cómo PastoreApp se adapta a tu iglesia.
          </p>
          <div className="pt-2 flex justify-center">
            <a
              href={WHATSAPP_MESSAGE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-black text-xs uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-emerald-500/20 active:scale-95 transition-all"
            >
              <MessageSquare size={16} />
              <span>Contactanos por WhatsApp</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
